---
title: "Order Types Explained: Market, Limit, Stop"
slug: order-types-explained
shortTitle: "Order Types Explained"
description: "Market orders fill now, limit orders wait for a better price, and stop orders wait for the price to break through a level. Learn when each one fills and what it costs you."
level: beginner
category: trading
course: beginner
order: 7
topics: [cfds, risk-management]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/order-types-explained.svg
faqs:
  - q: "What is the difference between a limit order and a stop order?"
    a: "A limit order waits for a better price than now: below the current price for a buy, above it for a sell. A stop order waits for the price to move through a level in the direction of the trade: above the current price for a buy, below it for a sell."
  - q: "Will my limit order always fill at my price?"
    a: "A limit order fills at your price or better, but only if the market reaches it. If the price never gets there, the order never fills and stays pending until you cancel it."
  - q: "Why did my stop order fill at a different price?"
    a: "Once a stop order's price is reached, it fills at the next available market price. In a fast market or after a gap that can be worse than the level you set, which is called slippage."
  - q: "Does a pending order use margin before it fills?"
    a: "Yes. On the platform, a Limit or Stop order reserves its margin as soon as you place it, which reduces your free margin. Canceling the order releases it."
  - q: "Can I place a stop-limit order?"
    a: "Not on the platform. You can place market orders, limit orders and stop orders, and attach a take profit and a stop loss to each one."
---

When you press **Buy** or **Sell**, you are sending an **order**: an instruction that says how and when you want to trade. The most basic order fills straight away at the current price. Others wait until the price reaches a level you choose, so you can plan an entry in advance instead of reacting in the moment.

This lesson covers the three order types you will use on {{BRAND}}, market, limit and stop, explains exactly when each one fills, and shows what can go wrong with each. Once these make sense, stop losses and take profits, which are close relatives, will too.

<CTA />

<KeyTakeaways>

- **Market orders fill now.** They fill at the current Ask for a buy or Bid for a sell, and the price can change slightly between click and fill.
- **Limit orders wait for a better price.** A buy below the current price or a sell above it, filled at your price or better, if the market gets there.
- **Stop orders wait for a breakout.** A buy above the current price or a sell below it, filled at market once triggered, so the fill can slip.
- **Pending orders tie up margin.** On {{BRAND}}, a Limit or Stop order reserves margin as soon as you place it.

</KeyTakeaways>

## Market orders: fill me now

A **market order** asks for the best price available right now. On {{BRAND}} you place one on the **Market** tab of the order panel. A buy fills at the **Ask** and a sell fills at the **Bid**, which is why it helps to know [how the spread works](/academy/article/bid-ask-spread).

Market orders are simple and almost always fill. The trade-off is that you do not control the exact price. In a calm market the fill is very close to the price you saw. In a fast one, the price can move between your click and the fill. The difference is called **slippage**, and it can go against you or occasionally in your favor.

Use a market order when getting into or out of a trade matters more than the last few points of price.

## Limit orders: only at this price or better

A **limit order** waits for a price that is better than the current one:

- A **Buy Limit** sits **below** the current Ask. It fills if the Ask falls to your price or lower.
- A **Sell Limit** sits **above** the current Bid. It fills if the Bid rises to your price or higher.

A limit order fills at your price **or better**. If the market jumps past your level, for example after a gap, you get the better price. The catch is that it may never fill at all: if the price turns back just before your level, you miss the trade.

Traders use limit orders to buy a pullback, sell into a rally, or enter at a level they have marked on the chart, such as the support and resistance zones covered in [Support and Resistance](/academy/article/support-and-resistance).

## Stop orders: once the price breaks through

A **stop order** waits for the price to move **through** a level in the direction of the trade:

- A **Buy Stop** sits **above** the current Ask. It triggers if the Ask rises to your price.
- A **Sell Stop** sits **below** the current Bid. It triggers if the Bid falls to your price.

Once triggered, a stop order fills **at market**. That makes it reliable, since it will fill, but not precise: in a fast move or after a gap, the fill can be worse than your stop price.

Traders use stop orders to join a move only once it gets going, often called trading a breakout or breakdown. The stop loss on an open position works on the same principle: it waits for the price to reach a level and then closes at market.

<CompareTable caption="The four pending order types on {{BRAND}}">

| Order | Placed | Fills when | Fill price | Typical idea |
| --- | --- | --- | --- | --- |
| Buy Limit | Below the Ask | The Ask falls to your price | Your price or better | Buy a dip more cheaply |
| Sell Limit | Above the Bid | The Bid rises to your price | Your price or better | Sell into a rise |
| Buy Stop | Above the Ask | The Ask rises to your price | Market, can slip | Buy a breakout |
| Sell Stop | Below the Bid | The Bid falls to your price | Market, can slip | Sell a breakdown |

</CompareTable>

## Worked examples

<Example title="Four orders on BTC/USDT (illustrative prices)">

BTC/USDT shows Bid 59,970 and Ask 60,030. Each order is for 0.05 BTC.

- **Buy Limit at 59,000.** Fills once the Ask falls to 59,000. If the price gaps straight to 58,800, it fills at 58,800, which is 200 × 0.05 = **10 USDT** better than planned.
- **Sell Limit at 62,000.** Fills once the Bid rises to 62,000.
- **Buy Stop at 61,000.** Triggers once the Ask reaches 61,000. In a fast move it might fill at 61,040: 40 × 0.05 = **2 USDT** of slippage.
- **Sell Stop at 58,500.** Triggers once the Bid falls to 58,500. After a sharp drop it might fill at 58,200: 300 × 0.05 = **15 USDT** worse than planned.

</Example>

## How {{BRAND}} chooses the order type for you

On {{BRAND}} you do not pick "limit" or "stop" from a list. You open the **Limit / Stop** tab, choose **Buy** or **Sell**, and type the price you want. The panel works out the type from where that price sits:

- Buy below the Ask: **Buy Limit**. Buy above the Ask: **Buy Stop**.
- Sell above the Bid: **Sell Limit**. Sell below the Bid: **Sell Stop**.

The order button always spells out what you are about to place, for example a Buy Limit at your price, so check it before you confirm. Stop-limit orders, which some platforms offer, are not available.

<Steps>
<Step title="Open the Limit / Stop tab">

On the trade screen, for example [BTC/USDT](/trade/BTC), choose **Buy** or **Sell**, then the **Limit / Stop** tab.

</Step>
<Step title="Enter your price and size">

Type the price you want, then the size in USDT or lots. Watch the label change between Limit and Stop as you move the price around the current Bid or Ask.

</Step>
<Step title="Add TP / SL and leverage">

Turn on **TP / SL** to attach a take profit and stop loss, and check the leverage on the **Leverage** button.

</Step>
<Step title="Read the summary and confirm">

Check **Margin required**, **Fee** and **Free margin after**, then confirm. The order appears in the **Pending Orders** tab until it fills or you cancel it.

</Step>
</Steps>

<Screenshot name="order-panel-limit-stop" alt="The Limit / Stop tab of the order panel with a price entered and the order type shown" caption="On the Limit / Stop tab, the price you enter decides whether the order is a Limit or a Stop." />

{/* TODO: verify with platform — that pending orders have no expiry and stay in Pending Orders until filled or canceled. */}

<ProTip>

A pending order reserves margin the moment you place it, even if it never fills. Old orders you have forgotten about quietly reduce your free margin. Review the **Pending Orders** tab regularly and cancel anything that no longer fits your plan. On desktop you can also drag a pending order's line on the chart to a new price.

</ProTip>

## Choosing between them

Ask yourself what matters most for this trade:

1. **Getting in now?** Use a market order and accept the current price, plus possible slippage.
2. **Getting a specific price?** Use a limit order and accept that it may never fill.
3. **Getting in only if the price proves itself?** Use a stop order and accept that the fill may slip.

<Warning>

Stop orders and stop losses fill at market once triggered. Around major news, at market opens, or when prices gap, the fill can be well beyond your chosen level. Size positions so that a worse-than-planned fill is still a loss you can accept.

</Warning>

## Conclusion

A market order fills now at the current Ask or Bid. A limit order waits for a better price and fills there or better, if it fills at all. A stop order waits for the price to break through a level and then fills at market, so it can slip. On {{BRAND}}, the **Limit / Stop** tab chooses the type from the price you enter, and every pending order reserves margin until it fills or you cancel it. Next, [Stop Loss and Take Profit](/academy/article/stop-loss-take-profit) shows how the same ideas protect a position once it is open.
